import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import auth from '@/router/auth';
import admin from '@/router/admin';
import stats from '@/router/stats';
import wiki from '@/router/wiki';

const Chat = () => import('@/views/Chat');

const Home = () => import('@/views/Home');
const Join = () => import('@/views/Join');
const Stats = () => import('@/views/Stats');
const Account = () => import('@/views/Account');
const Rules = () => import('@/views/Rules');
const Downloads = () => import('@/views/Downloads');
const Changelog = () => import('@/views/Changelog');
const Wiki = () => import('@/views/Wiki');
const Imprint = () => import('@/views/Imprint');
const DataPolicy = () => import('@/views/DataPolicy');
const Terms = () => import('@/views/Terms');
const CustomMainMenu = () => import('@/views/CustomMainMenu');
const Livestream = () => import('@/views/Livestream');
const Notification = () => import('@/views/Notifications.vue');

const PlayerView = () => import('@/views/PlayerView');
const Shop = () => import('@/views/Shop');

const NotFound = () => import('@/views/404');

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/auth',
    name: 'Auth',
    meta: {
      requiresNotLoggedIn: true,
    },
    children: auth,
  },
  {
    path: '/join',
    alias: ['/mitspielen', '/anleitung'],
    name: 'Join',
    component: Join,
  },
  {
    path: '/player/:id',
    name: 'Player',
    component: PlayerView,
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: Notification,
    meta: {
      requiresAuth: true,
      requiresPlayer: true,
    },
  },
  {
    path: '/chats',
    name: 'Chats',
    component: () => import('@/views/AllChats'),
    meta: {
      requiresAuth: true,
      requiresPlayer: true,
    },
  },
  {
    path: '/chat/:id',
    name: 'Chat',
    component: Chat,
    meta: {
      requiresAuth: true,
      requiresPlayer: true,
      disableBanner: true,
    },
  },
  {
    path: '/account',
    name: 'Account',
    component: Account,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/rules/:category?/:rule?',
    alias: [
      '/regeln/:category?/:rule?',
      '/regelwerk/:category?/:rule?',
      '/regles/:category?/:rule?',
    ],
    name: 'Rules',
    component: Rules,
    meta: {
      scrollScope: 'Rules',
    },
  },
  {
    path: '/downloads',
    name: 'Downloads',
    component: Downloads,
  },
  {
    path: '/changelog',
    name: 'Changelog',
    component: Changelog,
  },
  {
    path: '/wiki',
    name: 'Wiki',
    component: Wiki,
    meta: {
      scrollScope: 'Wiki',
    },
    children: wiki,
  },
  {
    path: '/imprint',
    name: 'Imprint',
    component: Imprint,
  },
  {
    path: '/policy',
    name: 'DataPolicy',
    component: DataPolicy,
  },
  {
    path: '/terms',
    name: 'Terms',
    component: Terms,
  },
  {
    path: '/admin',
    name: 'Admin',
    children: admin,
    meta: {
      requiresTeamMember: true,
      disableBanner: true,
    },
  },
  {
    path: '/stats',
    redirect: '/stats/community',
    name: 'Stats',
    component: Stats,
    meta: {
      scrollScope: 'Stats',
    },
    children: stats,
  },
  {
    path: '/custommainmenu',
    name: 'CustomMainMenu',
    component: CustomMainMenu,
  },
  {
    path: '/livestream',
    name: 'Livestream',
    component: Livestream,
  },
  {
    path: '/shop',
    name: 'Shop',
    component: Shop,
  },
  {
    path: '/:catchAll(.*)',
    name: '404',
    component: NotFound,
  },
];

const getScrollScope = (route) => {
  const scopedRecord = [...route.matched].reverse().find(record => record.meta.scrollScope);
  return scopedRecord?.meta.scrollScope ?? route.name;
};

const isQueryOnlyNavigation = (to, from) => {
  return to.path === from.path && to.hash === from.hash;
};

const isSameScrollScope = (to, from) => {
  return getScrollScope(to) === getScrollScope(from);
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (isQueryOnlyNavigation(to, from)) return false;
    if (to.hash) {
      return { el: to.hash };
    }
    if (isSameScrollScope(to, from)) return false;
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  try {
    await store.dispatch('refreshSession');
  } catch {
    // An unavailable backend is not a logout. API authorization still protects every operation.
    next();
    return;
  }
  if (to.matched.some(record => record.meta.requiresTeamMember)) {
    if (!store.getters.isTeamMember) {
      next({ name: 'Home' });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.requiresPlayer)) {
    if (!store.getters.playerID) {
      store.commit('setTempPath', to.path);
      next({ name: 'Account', query: { reason: 'not_verified' } });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.getters.isLoggedIn) {
      store.commit('setTempPath', to.path);
      next({ name: 'AuthLogin' });
    } else {
      next();
    }
  } else if (to.matched.some(record => record.meta.requiresNotLoggedIn)) {
    if (store.getters.isLoggedIn) {
      next({ name: 'Home' });
    } else {
      next();
    }
  } else {
    next();
  }
});

export default router;
